import { castArray, isNil } from 'lodash';

import { localeDetails } from '~/components/CmsMain/localeGlobals';
import { isoDateToUs, localTimeToViewTime } from '~/DateTimeUtils';
import { BOOL4_OPTIONS, COUNTRY_TO_STATE_MAP } from '~/Types';

export type ConfiguredFieldOption = { id: string; desc: string };

type ValueTextContext = {
  // The options a select or multiselect value is named from. Each caller passes the list its own screen
  // offers: the read-only field filters it by category, the claim bar takes the labels the payload sends.
  options?: ConfiguredFieldOption[];
  formatCurrency?: (amount: number) => string;
};

const optionLabel = (options: ConfiguredFieldOption[] = [], id: unknown): string =>
  options.find((option) => option.id === id)?.desc || '';

const valueOrBlank = (value: unknown): string => (isNil(value) ? '' : String(value));

/** The organization region's states as select options, or null where the region has no state list. */
export const getRegionStateOptions = (): ConfiguredFieldOption[] | null => {
  // localeGlobals is untyped JavaScript that declares localeDetails as null until the app sets it at startup.
  const region = (localeDetails as { locale: { region: string } } | null)?.locale.region ?? '';
  const statesMap = (COUNTRY_TO_STATE_MAP as Record<string, Record<string, string> | undefined>)[region];
  if (!statesMap) return null;
  return Object.keys(statesMap)
    .sort((s1, s2) => statesMap[s1].localeCompare(statesMap[s2]))
    .map((state) => ({ id: state, desc: statesMap[state] }));
};

const VALUE_TEXT_BY_FIELD_TYPE: Record<string, (value: unknown, context: ValueTextContext) => string> = {
  string: valueOrBlank,
  multiline_string: valueOrBlank,
  number: valueOrBlank,
  year: valueOrBlank,
  date: (value) => (value ? isoDateToUs(value as string) : ''),
  time: (value) => (value ? localTimeToViewTime(value as string) : ''),
  yes_no_na_unknown: (value) =>
    Object.hasOwn(BOOL4_OPTIONS, String(value)) ? (BOOL4_OPTIONS as Record<string, string>)[String(value)] : '',
  monetary: (value, { formatCurrency = String }) =>
    isNil(value) || value === '' ? '' : `${formatCurrency(value as number)}`,
  boolean: (value) => (isNil(value) ? '' : value ? 'Yes' : 'No'),
  select: (value, { options }) => (value ? optionLabel(options, value) : ''),
  multiselect: (value, { options }) =>
    value
      ? castArray(value)
          .map((id) => optionLabel(options, id))
          .filter(Boolean)
          .join(', ')
      : '',
  us_state_select: (value) => (value ? optionLabel(getRegionStateOptions() ?? [], value) : ''),
};

/** The configured field types whose read-only value is a line of text, as opposed to a component. */
export const TEXT_VALUE_FIELD_TYPES = Object.keys(VALUE_TEXT_BY_FIELD_TYPE);

/**
 * How a configured field's value reads as text: the one formatter the read-only custom field and the claim bar
 * both render, so a type's date format, label or currency reaches every screen together.
 */
export const configuredFieldValueText = (fieldType: string, value: unknown, context: ValueTextContext = {}): string =>
  VALUE_TEXT_BY_FIELD_TYPE[fieldType]?.(value, context) ?? '';
